// Registration is otherwise only reachable via app.tsx's async chunk (see the import there) —
// every other loadFederatedModule() caller needs remotes/shared scope registered too, and
// init() is idempotent, so make the dependency structural rather than positional.
import '../module-federation';
import { loadRemote } from '@module-federation/enhanced/runtime';

/**
 * Thin wrapper around enhanced-runtime's loadRemote(), which types its result
 * as `T | null`. Our remotes are all registered in src/module-federation.ts and
 * always resolve here, so callers (mostly React.lazy) can assume a defined result.
 */
export const loadFederatedModule = async <T = { default: any }>(id: string): Promise<T> => {
  try {
    const module = await loadRemote<T>(id);
    if (!module) {
      throw new Error(`Failed to load federated module: ${id}`);
    }
    return module;
  } catch (error) {
    // ponytail: runtime-core 0.11.4 caches a rejected remoteEntry fetch in
    // globalThis.__GLOBAL_LOADING_REMOTE_ENTRY__ forever — no public API clears one key
    // (only a full resetFederationGlobalInfo(), which would drop every OTHER loaded remote
    // too). Reach in and delete just this remote's entries so a retry re-fetches instead of
    // replaying the same rejection for the rest of the page's life. Re-check on an
    // @module-federation/runtime-core bump.
    const remoteName = id.split('/')[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const globalLoading = (window as any).__GLOBAL_LOADING_REMOTE_ENTRY__;
    if (globalLoading) {
      Object.keys(globalLoading)
        .filter((key) => key === remoteName || key.startsWith(`${remoteName}:`))
        .forEach((key) => delete globalLoading[key]);
    }
    throw error;
  }
};
